import { memo, startTransition, useEffect } from 'react';
import { useIsClient } from 'usehooks-ts';

import {
  startAppConnectionCheck,
  startHealthCheck,
} from '@store/actions/action-health';
import { useAppDispatch, useAppSelector, useAppStore } from '@store/hooks';
import { useEntitlement } from '@components/entitlements';
import { getSystemLimits } from '@features/limits/limits.slice';
import { useSessionValidator } from '@hooks/use-session-validator';

import { useAppContext } from './app-context';
import socketListenerInit from './socket-listener-init';

/**
 * This handles client-side initialization tasks such as registering socket
 * listeners. The component is memoized to prevent unnecessary re-renders and
 * does not return any DOM elements, so it is unlikely to ever be remounted.
 * Any external changes that it needs to react to are handled internally within
 * effect hooks.
 */
export const ClientInit = () => {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const isClient = useIsClient();

  // Validate session and logout if invalid
  useSessionValidator();

  const { isWebSocketOnly } = useAppContext();

  const { isAuthenticated, account } = useAppSelector(state => state.auth);

  const crpEnabled = useEntitlement('crp');

  const {
    isEngineHealthy,
    isLicenseGood,
    isAppDBHealthy,
    isEngineVersionGood,
    isReportsEnabledAndActive,
  } = useAppSelector(state => state.app.healthCheck);

  // Add and remove operations based on authentication state
  useEffect(() => {
    if (!isClient) return;

    // Instantiate the websocket client binding if it's not already present
    if (
      !window.sock &&
      window.io &&
      isLicenseGood &&
      isLicenseGood?.isBeforeExpiration
    ) {
      // Initialize all the various socket message listeners
      window.sock = socketListenerInit({
        props: {
          isAppDBHealthy,
          isAuthenticated,
          isEngineHealthy,
          isEngineVersionGood,
          isReportsEnabledAndActive,
        },
        isWebSocketOnly,
        startAppConnectionCheck,
        startHealthCheck,
        store,
      });
    }

    if (isAuthenticated && isLicenseGood && isLicenseGood?.isBeforeExpiration) {
      // This condition adds the user- and account-specific socket channels
      // after authentication, or restores them after a page refresh within an
      // authenticated session
      if (account?.sessionID) {
        // Room scoped to the session ID (private to this user) and room
        // scoped to the account name (private to this account)
        window.sock
          ?.emit('addToRoom', account.sessionID)
          .emit('addToRoom', `account-${account.accountname}`);
        if (account.isAdmin) {
          // Room scoped to all administrators (users with the system-admin role)
          window.sock?.emit('addToRoom', `system-admins`);
        }
      }

      // Initialize Prism if it's not already loaded
      if (!window.Prism) {
        import('../../public/prism/prism');
      }

      // Configure the Pace progress bar and then initialize if it's not
      // already present
      if (!window.Pace) {
        window.paceOptions = {
          elements: false,
          restartOnRequestAfter: false,
        };
        import('../../public/pace/pace.min');
      }
    } else {
      window.Pace?.stop();
    }

    // Remove the socket listeners if the component unmounts
    return () => {
      window.sock
        ?.removeAllListeners('connect')
        .removeAllListeners('connect_error');
    };
  }, [
    isClient,
    isAuthenticated,
    isLicenseGood && isLicenseGood?.isBeforeExpiration,
  ]);

  // Fetch system limits on initial load
  useEffect(() => {
    if (!isAuthenticated) return;

    startTransition(() => {
      dispatch(getSystemLimits({ crpEnabled }));
    });
  }, [isAuthenticated]);

  return null;
};

export default memo(ClientInit);
